import { AloftDate } from '@aloft/toolkit-shared';
import { Block, Flex, Input, Typography, colors } from '@aloft/toolkit-sui';

import { Controller, useWatch } from 'react-hook-form';

import { useZodFormContext } from '../../../../hooks/useZodForm.ts';
import { RecommendCompsFormSchema } from '../../types.ts';
import { filterHasValue } from '../../utils/filterHasValue.ts';
import { EMPTY_RANGE_FILTER, FilterSection, WithinRangeMode } from './FilterSection.tsx';

const MODE_TO_MATCH_CRITERIA: Record<WithinRangeMode, 'lasting_range' | 'min_max_range'> = {
  within: 'lasting_range',
  range: 'min_max_range',
};

const EMPTY_DATE_NATIVE_SX = {
  '::-webkit-datetime-edit': { color: 'transparent' },
  '::-webkit-datetime-edit-text': { color: 'transparent' },
  '::-webkit-datetime-edit-month-field': { color: 'transparent' },
  '::-webkit-datetime-edit-day-field': { color: 'transparent' },
  '::-webkit-datetime-edit-year-field': { color: 'transparent' },
} as const;

const SaleDateCalendarIcon = () => (
  <svg width={20} height={20} viewBox="0 0 20 20" fill="none" aria-hidden>
    <path
      d="M14.166 2.49992H17.4993C17.7204 2.49992 17.9323 2.58772 18.0886 2.744C18.2449 2.90028 18.3327 3.11224 18.3327 3.33325V16.6666C18.3327 16.8876 18.2449 17.0996 18.0886 17.2558C17.9323 17.4121 17.7204 17.4999 17.4993 17.4999H2.49935C2.27834 17.4999 2.06637 17.4121 1.91009 17.2558C1.75381 17.0996 1.66602 16.8876 1.66602 16.6666V3.33325C1.66602 3.11224 1.75381 2.90028 1.91009 2.744C2.06637 2.58772 2.27834 2.49992 2.49935 2.49992H5.83268V0.833252H7.49935V2.49992H12.4993V0.833252H14.166V2.49992ZM12.4993 4.16659H7.49935V5.83325H5.83268V4.16659H3.33268V7.49992H16.666V4.16659H14.166V5.83325H12.4993V4.16659ZM16.666 9.16658H3.33268V15.8333H16.666V9.16658Z"
      fill={colors.textLight}
    />
  </svg>
);

const SaleDateWithinField = ({
  value,
  onChange,
}: {
  value: number | '';
  onChange: (value: number | '') => void;
}) => (
  <Flex
    alignItems="center"
    gap="8px"
    height="40px"
    paddingLeft="12px"
    paddingRight="8px"
    borderWidth="1px"
    borderStyle="solid"
    borderColor="strokeDefault"
    borderRadius="4px"
    backgroundColor="surface1"
    width="100%"
  >
    <Block flexShrink={0} width="32px">
      <Input
        id="saleDate-variance"
        size="sm"
        type="number"
        value={value}
        onChange={onChange}
        onClear={() => onChange('')}
        clearable={false}
        border="hidden"
        boxShadow="none!important"
        __dangerDoNotUseSx={{ paddingInline: 0 }}
      />
    </Block>
    <Typography variant="body2" color="textTertiary">
      months (from Effective Date)
    </Typography>
  </Flex>
);

const SaleDateRangeInput = ({
  id,
  value,
  onChange,
}: {
  id: string;
  value: AloftDate | '';
  onChange: (value: AloftDate | '') => void;
}) => (
  <Block position="relative">
    <Input
      id={id}
      size="sm"
      type="aloftDate"
      value={value}
      onChange={onChange}
      onClear={() => onChange('')}
      __dangerDoNotUseSx={{
        position: 'relative',
        paddingRight: '36px',
        ...(!value ? EMPTY_DATE_NATIVE_SX : {}),
        '::-webkit-calendar-picker-indicator': {
          opacity: 0,
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          cursor: 'pointer',
        },
      }}
    />
    {!value && (
      <Flex
        position="absolute"
        left="12px"
        top="0"
        bottom="0"
        alignItems="center"
        __dangerDoNotUseSx={{ pointerEvents: 'none' }}
      >
        <Typography variant="body2" color="textDisabled">
          mm/dd/yy
        </Typography>
      </Flex>
    )}
    <Flex
      position="absolute"
      right="8px"
      top="0"
      bottom="0"
      alignItems="center"
      __dangerDoNotUseSx={{ pointerEvents: 'none' }}
    >
      <SaleDateCalendarIcon />
    </Flex>
  </Block>
);

export const SaleDateFilter = () => {
  const { control, setValue } = useZodFormContext<RecommendCompsFormSchema>();
  const matchCriteria = useWatch({ control, name: 'search.matchCriteria.saleDate' });
  const filterValue = useWatch({ control, name: 'search.filters.saleDate' });
  const mode: WithinRangeMode = matchCriteria === 'min_max_range' ? 'range' : 'within';

  const handleModeChange = (nextMode: WithinRangeMode) => {
    setValue('search.filters.saleDate', { ...EMPTY_RANGE_FILTER });
    setValue('search.matchCriteria.saleDate', MODE_TO_MATCH_CRITERIA[nextMode]);
  };

  return (
    <FilterSection
      label="Sale Date"
      mode={mode}
      onModeChange={handleModeChange}
      showClear={filterHasValue(filterValue)}
      onClear={() => setValue('search.filters.saleDate', { ...EMPTY_RANGE_FILTER })}
    >
      {mode === 'within' ? (
        <Controller
          name="search.filters.saleDate.variance"
          control={control}
          render={({ field }) => (
            <SaleDateWithinField value={field.value ?? ''} onChange={field.onChange} />
          )}
        />
      ) : (
        <Flex gap="8px" minWidth={0}>
          <Block flex={1} minWidth={0}>
            <Controller
              name="search.filters.saleDate.min"
              control={control}
              render={({ field }) => (
                <SaleDateRangeInput
                  id="saleDate-min"
                  value={(field.value ?? '') as AloftDate | ''}
                  onChange={field.onChange}
                />
              )}
            />
          </Block>
          <Block flex={1} minWidth={0}>
            <Controller
              name="search.filters.saleDate.max"
              control={control}
              render={({ field }) => (
                <SaleDateRangeInput
                  id="saleDate-max"
                  value={(field.value ?? '') as AloftDate | ''}
                  onChange={field.onChange}
                />
              )}
            />
          </Block>
        </Flex>
      )}
    </FilterSection>
  );
};
